import { Metrics } from "../analytics/Metrics";
import Button from "../core/Button";
import Strings from "../core/data/Strings";
import ResizableScene from "../core/ResizableScene";
import ScaleManager from "../core/ScaleManager";
import SoundButton from "../core/SoundButton";
import Decoration from "../game/Decoration";
import TransformController from "../game/TransformController";
import GameColors from "../GameColors";
import SessionData from "../SessionData";
import DecorationMenu from "../ui/DecorationMenu";
import Photo from "../ui/Photo";
import QuitPopup from "../ui/QuitPopup";
import Recap from "../ui/Recap";
import RestartPopup from "../ui/RestartPopup";
import Fonts from "../utils/Fonts";

export class GameScene extends ResizableScene {
	public static readonly ADDED_DECORATION = "addedDecoration";
	public static readonly CHANGED_PAGE = "changedPage";
	public static readonly ON_SNAPSHOT = "onSnapshot";
	public static readonly ON_DESELECT = "onDeselect";
	public static readonly ON_REDECO = "onRedeco";

	transformController: TransformController;
	protected hudContainer: Phaser.GameObjects.Container;

	protected selectedDecoration: Decoration | null = null;
	protected menu: DecorationMenu;
	photo: Photo;
	headerText:Phaser.GameObjects.Text;
	header: Phaser.GameObjects.Image;
	frame: Phaser.GameObjects.Image;
	decorations: Decoration[]=[];
	tweenOutButtons: Button[] = [];
	photoScale: number;
	logoContainer: Phaser.GameObjects.Container;
	soundButton: SoundButton;

	create(): void {
		super.create();
		Metrics.analytics.logEvent("visit_screen", { game: "ag_photobooth", screen: "photo_edit" });
		const bg = this.add.image(0, 0, "gameplayBg");
		const corkBg = this.add.image(0, 0, "corkBg");
		const photoScale = this.photoScale = 1.1;

		const photo = this.photo = new Photo(this, SessionData.Instance.selectedPhoto, true, true);
		this.add.existing(photo);
		photo.scale = photoScale;

		const menu = this.menu = new DecorationMenu(this);
		this.add.existing(menu);
		menu.on(DecorationMenu.ON_DECO_SPAWN, this.addDecoration, this);

		const divider = this.add.image(0, 0, "divider");
		divider.alpha = 0;

		const header = this.header = this.add.image(0, 0, "sideHeader");
		this.add.existing(header);

		const headerText = this.headerText = this.add.text(0, 0, Strings.get("Dolls"), {
			fontFamily: Fonts.LunchBoxRegular, 
			color: GameColors.WHITE,
			fontSize: "32px"
		});
		this.add.existing(headerText);
		
		const backbutton = this.add.button(0, 0, "homeBtn", 0, this.showQuitPopup, this);

		const soundImg = this.sound.mute ? "soundBtnOff" : "soundBtnOn";
        const soundButton = this.soundButton = new SoundButton(this, 0, 0, soundImg, (mute) => {
            soundButton.setTexture(mute ? "soundBtnOff" : "soundBtnOn");
        });
        this.add.existing(soundButton);

		const photoCompleteButton = this.add.button(0, 0, "nextBtn", undefined, this.onPhotoComplete, this, true);
		const undoButton = this.add.button(0, 0, "undoBtn", undefined, this.restart, this, true);
		const logo = this.add.image(0, 0, "recapLogoDeco");
		const ideal = 1475 / 900;
		let ratio = this.viewport.width / this.viewport.height;
		let scale = (ideal > ratio) ? 1 - (ideal - ratio) : 1;
		logo.setScale(0.75 * scale);
		const logoContainer = this.logoContainer = this.add.container();
		logoContainer.setSize(logo.displayWidth, logo.displayHeight);
		logoContainer.add(logo);

		this.hudContainer = this.add.container();

		this.transformController = new TransformController(this, { minScale: 0.5, maxScale: 1.5 });
		this.hudContainer.add(this.transformController);
		this.transformController.setActive(false);

		this.transformController.on(TransformController.ON_DELETE, this.removedSelected, this);
		this.transformController.on(TransformController.ON_CONFIRM, () => this.deselectDecoration(), this);
		this.addResize(() => {
			ScaleManager.ScaleBackgroundFromZone(bg, this.viewport);
			this.alignIn.Center(bg, this.viewport, 0, 0);
			this.alignIn.LeftCenter(corkBg, this.viewport, 0, 0);
			this.alignIn.RightCenter(divider, corkBg, 42, 0);

			const yOffset = -32 * 2 - backbutton.height;
			// const height = (this.viewport.height + yOffset - 32) / menu.height;

			// const xOffset = (menu.displayHeight - menu.height) / 2;
			this.alignIn.Center(menu, corkBg, -8, 20);
			menu.resize();

			// const remainingWidth = this.viewport.width - menu.displayWidth - 32 * 2;
			// const remainingHeight = this.viewport.height - 32 * 2;

			// const photoXOffset = (photo.displayWidth - photo.width) / 2;
			// const photoYOffset = (photo.displayHeight - photo.height) / 2;
			this.alignIn.Center(photo, this.viewport, 185, -50);

			const screenRatio = this.viewport.width / this.viewport.height;
            const idealRatio = 15.5/9;
			let scale = 1;
			if (screenRatio < idealRatio) scale = 1 - (idealRatio - screenRatio);
			photo.scale = 1.1 * scale;
			photo.resize();

			this.alignIn.TopCenter(header, menu, 62, 55);
			this.alignIn.Center(headerText, header);

			this.alignIn.TopRight(soundButton, this.viewport, -18, -15);
			this.alignIn.TopLeft(backbutton, this.viewport, -18, -15);

			this.alignIn.BottomCenter(photoCompleteButton, this.viewport, 180, -10);
			this.alignTo.LeftCenter(undoButton, photoCompleteButton, 10, 0);

			ratio = this.viewport.width / this.viewport.height;
			scale = (ideal > ratio) ? 1 - (ideal - ratio) : 1;
			logo.setScale(0.75 * scale);
			logoContainer.setSize(logo.displayWidth, logo.displayHeight);
			const dif = (logo.height * 0.75 - logo.displayHeight)/2;
			this.alignIn.BottomRight(logoContainer, this.viewport, -20 + dif * 2, -17 - dif);
		});
		this.resize();

		const adjustedPhotoScale = photo.scale;

		// Tween build in
		const scaleElements = [photo, soundButton, backbutton, undoButton, photoCompleteButton];
		for (const element of scaleElements) element.setScale(0);

		const menuElements = [corkBg, divider, header, headerText, menu, menu.maskImg];
		const offset = corkBg.displayWidth + divider.displayWidth;
		for (const element of menuElements) element.x -= offset;
		headerText.setAlpha(0);

		for (const button of menu.categoryButtons) button.setVisible(false);

		logoContainer.x += 250;

		const duration = 400;
		const delay = 500;

		this.tweens.add({
			targets: photo,
			scale: adjustedPhotoScale,
			duration: duration,
			delay: delay,
			ease: Phaser.Math.Easing.Back.Out,
			onUpdate: ()=>{
				photo.updateMaskPosition();
			}
		});

		for (const element of menuElements) {
			this.tweens.add({
				targets: element,
				x: element.x + offset,
				duration: duration,
				delay: delay,
				ease: Phaser.Math.Easing.Quadratic.Out,
			});
		}

		this.tweens.add({
			targets: [backbutton, soundButton, undoButton],
			scale: 1,
			duration: duration,
			delay: delay + duration/2,
			ease: Phaser.Math.Easing.Back.Out,
		});
		this.tweens.add({
			targets: photoCompleteButton,
			scale: 1,
			duration: duration,
			delay: delay + duration/2 + 100,
			ease: Phaser.Math.Easing.Back.Out,
		});
		this.tweens.add({
			targets: logoContainer,
			x: logoContainer.x - 250,
			duration: duration,
			delay: delay + duration/2 + 100,
			ease: Phaser.Math.Easing.Quadratic.Out,
		});

		this.time.addEvent({
			delay: delay + duration,
			callback: () => {
				for (let i = 0; i < menu.categoryButtons.length; i++) {
					const button = menu.categoryButtons[i];
					button.x += button.displayWidth;
					button.setVisible(true);
					this.tweens.add({
						targets: button,
						x: button.x - button.displayWidth,
						duration: duration,
						ease: Phaser.Math.Easing.Back.Out,
						delay: 100 * i,
					});
				}

				this.tweens.add({
					targets: headerText,
					alpha: 1,
					duration: duration,
					ease: Phaser.Math.Easing.Quadratic.Out
				});
			}
		});

		this.tweenOutButtons = [backbutton, undoButton, photoCompleteButton];
	}

	recapTweenOut(onComplete: Function): void {
		for (let i = 0; i < this.menu.categoryButtons.length; i++) {
			const button = this.menu.categoryButtons[i];
			
			this.tweens.add({
				targets: button,
				x: button.xPosition + button.displayWidth,
				duration: 300,
				ease: Phaser.Math.Easing.Back.Out,
				delay: 75 * i,
			});
		}

		for (let i = 0; i < this.menu.decoButtons.length; i++) {
			const button = this.menu.decoButtons[i];
			this.tweens.add({
				targets: button,
				scale: 0,
				duration: 300,
				ease: Phaser.Math.Easing.Back.In,
			});
		}

		for (let i = 0; i < this.tweenOutButtons.length; i++) {
			const button = this.tweenOutButtons[i];
			this.tweens.add({
				targets: button,
				scale: 0,
				duration: 300,
				ease: Phaser.Math.Easing.Back.In,
				delay: 75 * i,
			});
		}

		this.tweens.add({
			targets: this.logoContainer,
			x: this.logoContainer.x + 250,
			duration: 300,
			delay: (this.tweenOutButtons.length - 1) * 75,
			ease: Phaser.Math.Easing.Quadratic.In,
		});

		this.tweens.add({
			targets: [this.menu, this.menu.maskImg, this.header],
			scale: 0,
			duration: 300,
			ease: Phaser.Math.Easing.Quadratic.In,
			delay: 300,
			onComplete: () => onComplete()
		});

		this.tweens.add({
			targets: this.headerText,
			alpha: 0,
			duration: 300,
			ease: Phaser.Math.Easing.Quadratic.In,
		});
	}

	resumeFromRecap(): void {
		Metrics.analytics.logEvent("visit_screen", { game: "ag_photobooth", screen: "photo_edit" });
		this.menu.scrollRect.setProgress(0);
		for (let i = 0; i < this.menu.categoryButtons.length; i++) {
			const button = this.menu.categoryButtons[i];
			this.tweens.add({
				targets: button,
				x: button.activeCategory ? button.xPosition : button.outPosition,
				duration: 300,
				ease: Phaser.Math.Easing.Back.Out,
				delay: 300 + 75 * i,
			});
		}

		for (let i = 0; i < this.menu.decoButtons.length; i++) {
			const button = this.menu.decoButtons[i];
			this.tweens.add({
				targets: button,
				scale: 1,
				duration: 300,
				ease: Phaser.Math.Easing.Back.Out,
				delay: (i <= 2) ? 100 : 400,
			});
		}

		for (let i = 0; i < this.tweenOutButtons.length; i++) {
			const button = this.tweenOutButtons[i];
			this.tweens.add({
				targets: button,
				scale: 1,
				duration: 300,
				ease: Phaser.Math.Easing.Back.Out,
				delay: 75 * i,
			});
		}

		this.logoContainer.scale = 0;
		this.tweens.add({
			targets: this.logoContainer,
			scale: 1,
			duration: 300,
			delay: this.tweenOutButtons.length * 75,
			ease: Phaser.Math.Easing.Quadratic.Out,
		});

		this.tweens.add({
			targets: [this.menu, this.header],
			scale: 1,
			duration: 300,
			ease: Phaser.Math.Easing.Quadratic.Out,
			onUpdate: () => {
				this.menu.maskImg.setDisplaySize(this.menu.scrollInner.displayWidth/this.menu.scale, (this.menu.scrollInner.displayHeight - 6)/this.menu.scale);
				this.resize();
			},
		});

		this.tweens.add({
			targets: this.headerText,
			alpha: 1,
			duration: 300,
			delay: 200,
			ease: Phaser.Math.Easing.Quadratic.Out,
			onComplete: () => {
				this.setButtonsEnabled(true);
			}
		});
	}

	alignHeader(): void{
		this.alignIn.Center(this.headerText, this.header);
	}

	update(dt: number): void {
		dt = dt / 1000;
		this.menu?.update(dt);
	}

	addDecoration(decoration: Decoration): void {
		// Spawn Decoration
		// this.hudContainer.add(decoration);
		this.events.emit(GameScene.ADDED_DECORATION);
		this.selectDecoration(null, decoration);

		decoration.on(
			Decoration.ON_DRAG_START,
			(ptr: Phaser.Input.Pointer, dec: Decoration) => this.selectDecoration(ptr, dec, false, true),
			this
		);
		decoration.on(Decoration.ON_DRAG_END, this.returnedToMenu, this);
		decoration.on(Decoration.ON_CLICK, (ptr: Phaser.Input.Pointer, dec: Decoration) => this.selectDecoration(ptr, dec, true), this);
		this.decorations.push(decoration);
	}

	selectDecoration(ptr: Phaser.Input.Pointer, decoration: Decoration, click = false, drag = false): void {
		if (decoration == null || decoration == undefined) return;
		if (this.selectedDecoration != null && this.selectedDecoration != undefined) {
			const sameDecoration = this.selectedDecoration == decoration;
			if (!sameDecoration) this.deselectDecoration();
			else if (click) {
				this.deselectDecoration(true);
				return;
			} else {
				// started dragging the same decoration
				return;
			}
		}

		const itemName = decoration.decorationData.name ?? decoration.decorationData.id;
		Metrics.analytics.logEvent("select_item", { game: "ag_photobooth", category: decoration.category, item: itemName });

		if (click) this.soundManager.play("click");
		this.selectedDecoration = decoration;
		this.photo.removeDecoration(this.selectedDecoration, this.selectedDecoration.sticker);
		if (decoration.masked) decoration.mask = this.photo.photoMask;

		this.transformController.setTransformObject(this.selectedDecoration);
		this.selectedDecoration.setTransformSettings(this.transformController);
		if (drag) this.transformController.startDrag(ptr);
	}

	deselectDecoration(onClick = false): void {
		if (this.selectedDecoration == null) return;
		const deco = this.selectedDecoration;

		this.events.emit(GameScene.ON_DESELECT);

		if (onClick) this.soundManager.play("itemPlaced");

		let destroyed = false;
		if (this.overMenu() || !this.overlapsPhoto(deco)) {
			this.transformController.removeTransformObject();
			this.removeDecorationListeners(deco);
			deco.destroy();
			destroyed = true;
		} else if (!deco.input.enabled) {
			// Renable Input if it isn't
			deco.enableInput(true);
		}

		this.selectedDecoration = null;
		this.transformController.setActive(false);
		if (!destroyed) {
			this.photo.addDecoration(deco, deco.sticker);
			if (deco.masked) deco.mask = null;
		}
	}

	returnedToMenu(): void {
		if (this.overMenu()) {
			this.transformController.removeTransformObject();
			this.removeDecorationListeners(this.selectedDecoration);
			this.selectedDecoration.destroy();

			this.selectedDecoration = null;
			this.transformController.setActive(false);
		}
	}

	overMenu(): boolean {
		const width = this.menu.displayWidth + 32;
		return this.transformController.x < width;
	}

	overlapsPhoto(deco: Decoration): boolean {
		if (!deco) return false;
		const decoBounds = deco.getWorldBounds();
		const photoBounds = this.photo.getWorldBounds();
		return Phaser.Geom.Rectangle.Overlaps(decoBounds, photoBounds);
	}

	removeAllDecorations(): void{
		for (const deco of this.decorations){
			this.removeDecorationListeners(deco);
			deco.destroy();
		}
		this.decorations = [];
		this.transformController.trash();
		this.photo.setFrame();
		this.photo.setFilter();
	}

	removedSelected(decoration?: Decoration): void {
		this.removeDecorationListeners(decoration ? decoration: this.selectedDecoration);
		this.selectedDecoration = null;
	}

	removeDecorationListeners(decoration: Decoration): void {
		decoration.off(Decoration.ON_DRAG_START, this.selectDecoration, this);
		decoration.off(Decoration.ON_DRAG_END, this.returnedToMenu, this);
		decoration.off(Decoration.ON_CLICK);
	}

	restart(): void{
		const pop = new RestartPopup(this, () => {this.removeAllDecorations();}, this);
		this.add.existing(pop);
	}

	onPhotoComplete(): void {
		this.deselectDecoration();
		this.setButtonsEnabled(false);
		const texturekey = "snapshot";
		if (this.textures.exists(texturekey)) {
			this.textures.remove(texturekey);
		}
		this.photo.savePhoto(texturekey, (img)=>{
			this.recapTweenOut(() => {
				const recap = new Recap(this, img);
				this.add.existing(recap);
			});
		});
	}

	showQuitPopup(): void {
		const popup = new QuitPopup(this);
		this.add.existing(popup);
	}

	shutdown(): void {
		this.menu = undefined;
		this.transformController = undefined;
		this.hudContainer = undefined;
		this.header = null;
		this.headerText = null;
		this.selectedDecoration = null;
		this.photo = undefined;
		if (this.textures.exists("snapshot")) {
			this.textures.remove("snapshot");
		}
		super.shutdown();
	}

	setButtonsEnabled(enable: boolean): void {
		if (enable) for (const button of this.tweenOutButtons) button.setInteractive();
		else for (const button of this.tweenOutButtons) button.disableInteractive();
	}
}